// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BarChartCard } from '@/components/bar-chart-card';
import { ASSET_FUNCTIONAL_STATUS_LABELS } from '@/constants/asset-functional-status';
import { LocationFunctionalStatusReport } from '@/location/api/get-location-report';
import {
  LocationReportSort,
  useLocationReport,
} from '@/location/hooks/use-location-report';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { useTheme } from '@mui/material';
import { LinkProps } from '@tanstack/react-router';

type Key = Extract<
  keyof LocationFunctionalStatusReport,
  'functionalCount' | 'needsReviewCount' | 'nonFunctionalCount'
>;

const KEYS: Key[] = [
  'functionalCount',
  'needsReviewCount',
  'nonFunctionalCount',
];

function getLabelForKey(key: Key) {
  switch (key) {
    case 'functionalCount':
      return ASSET_FUNCTIONAL_STATUS_LABELS['FUNCTIONAL'];

    case 'needsReviewCount':
      return ASSET_FUNCTIONAL_STATUS_LABELS['NEEDS_REVIEW'];

    case 'nonFunctionalCount':
      return ASSET_FUNCTIONAL_STATUS_LABELS['NON_FUNCTIONAL'];
  }
}

function getHref(datum: LocationFunctionalStatusReport, key: Key): LinkProps {
  switch (key) {
    case 'functionalCount':
      return {
        to: '/assets',
        search: {
          locations: [datum.location.id],
          functionalStatuses: ['FUNCTIONAL'],
        },
      };

    case 'needsReviewCount':
      return {
        to: '/assets',
        search: {
          locations: [datum.location.id],
          functionalStatuses: ['NEEDS_REVIEW'],
        },
      };

    case 'nonFunctionalCount':
      return {
        to: '/assets',
        search: {
          locations: [datum.location.id],
          functionalStatuses: ['NON_FUNCTIONAL'],
        },
      };
  }
}

interface Props {
  sort: LocationReportSort;
}

export function LocationFunctionalStatusReportCard(props: Props) {
  const theme = useTheme();
  const { functionalStatusData, isLoading } = useLocationReport(props.sort);

  const colors = [
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];
  const emptyState = isLoading
    ? 'Loading'
    : 'There is no location data to display';

  return (
    <BarChartCard
      title="Asset status by location"
      emptyState={emptyState}
      keys={KEYS}
      data={functionalStatusData}
      colors={colors}
      getHref={getHref}
      getLabelForKey={getLabelForKey}
      getLabelForDatum={(datum) => getEffectiveLocationName(datum.location)}
      pluralizeTooltipOptions={{ singular: 'asset' }}
    />
  );
}
